/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { RadixSelectControl } from "../../ui/radix-select";
import { useNodeLeftSiteNav } from "../hooks";
import { ISiteGroup } from "../../../store/api/devices/types";

const AddRemoveSiteGroup: React.FC = () => {

  const {
    dispatch,
    currentSiteGroups,
    deviceToSiteGroupHandler,
    devicesToSiteGroupData,
    removeDeviceFromSiteGroupHandler,
    handleMoveDeviceToSiteGroup,
    selectedGeoRegion,
    geoRegions,
    setDevicesToSiteGroupData,
  } = useNodeLeftSiteNav();

  const [selectedRegionKey, setSelectedRegionKey] = useState<string>("");

  useEffect(() => {
    if (!selectedRegionKey) {
      const defaultRegionKey =
        (selectedGeoRegion?.objectKey && currentSiteGroups?.some((group: ISiteGroup) => group.parentKey === selectedGeoRegion.objectKey))
          ? selectedGeoRegion.objectKey
          : (currentSiteGroups && currentSiteGroups[0]?.parentKey) || "";
      setSelectedRegionKey(defaultRegionKey);
    }
  }, [selectedRegionKey, selectedGeoRegion, currentSiteGroups]);

  const isEnable = !!devicesToSiteGroupData?.groupkey;

  const handleRegionChange = (value: string) => {
    setSelectedRegionKey(value || "");
    setDevicesToSiteGroupData(undefined);
  };

  const handleSiteGroupChange = (value: string) => {
    const syntheticEvent = { preventDefault: () => {}, target: { value } };
    handleMoveDeviceToSiteGroup(syntheticEvent);
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const filteredSiteGroups: ISiteGroup[] =
    currentSiteGroups?.filter((group: ISiteGroup) => group.parentKey === selectedRegionKey) || [];

  const regionOptions = (geoRegions || [])
    .filter((region) => currentSiteGroups?.some((group: ISiteGroup) => group.parentKey === region.objectKey))
    .map((region) => ({
      label: region.name,
      value: region.objectKey,
    }));

  const siteGroupOptions = filteredSiteGroups.map((group: ISiteGroup) => ({
    label: group.name,
    value: group.objectKey,
  }));

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button disabled={!isEnable} onClick={deviceToSiteGroupHandler}>
        Add
      </Button>
      <Button variant="secondary" disabled={!isEnable} onClick={removeDeviceFromSiteGroupHandler}>
        Remove
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title="Site Group" footer={footer} width="420px">
      <div className="space-y-5 pt-2">
        <div className="space-y-2">
          <Label>Regions</Label>
          <RadixSelectControl
            options={regionOptions}
            value={selectedRegionKey || ""}
            onValueChange={handleRegionChange}
            placeholder="Select a region"
            autoFocus={true}
          />
        </div>
        <div className="space-y-2">
          <Label>Site Groups</Label>
          <RadixSelectControl
            options={siteGroupOptions}
            value={devicesToSiteGroupData?.groupkey || ""}
            onValueChange={handleSiteGroupChange}
            placeholder="Select a site group"
          />
        </div>
      </div>
    </Sheet>
  );
};
export default AddRemoveSiteGroup;
